/**
 * Conectores de IA (SPEC-20261103-1818-conector-do-claude).
 *
 * O Orbind é um conector MCP do Claude: a pessoa instala o conector no Claude
 * dela e passa a consultar, alterar, comentar, concluir e criar Itens de
 * Trabalho de dentro da conversa. O conector age COMO A PESSOA — com a
 * visibilidade e o perfil dela — e, por cima disso, com o que o administrador
 * do workspace liberou aqui.
 *
 * Puro de propósito: vocabulário que API e telas precisam falar igual.
 */

/* ---------------------------------------------------------- permissões --- */

/**
 * O que o administrador libera por pessoa. Vocabulário fechado, como os
 * escopos dos plugins: permissão desconhecida é recusa, não "ignora".
 *
 * `consultar` é a base: sem ela o conector não serve para nada, e a tela não
 * deixa desmarcar. As outras são de escrita, e cada uma é uma ferramenta do
 * conector que passa a existir.
 */
export const PERMISSOES_DO_CONECTOR = ['consultar', 'alterar', 'comentar', 'concluir', 'criar', 'campos'] as const
export type PermissaoDoConector = (typeof PERMISSOES_DO_CONECTOR)[number]

export const PERMISSAO_DO_CONECTOR_ROTULO: Record<PermissaoDoConector, string> = {
  consultar: 'Consultar os Itens de Trabalho que a pessoa vê',
  alterar: 'Alterar título, descrição, prazo, prioridade, responsável, etapa, checklists e campos complementares',
  comentar: 'Comentar nos Itens',
  concluir: 'Mudar o status, inclusive concluir (dar baixa)',
  criar: 'Criar Itens de Trabalho novos',
  // SPEC-20261103-2009: mexe no CADASTRO do workspace, por isso separada e só para quem administra
  campos: 'Criar campos complementares (só quem administra o workspace)',
}

/** O conjunto que um acesso novo recebe: tudo, menos criar — criar é opt-in. */
export const PERMISSOES_PADRAO_DO_CONECTOR: readonly PermissaoDoConector[] = [
  'consultar',
  'alterar',
  'comentar',
  'concluir',
]

/** Normaliza o que veio do banco/pedido: só o vocabulário, sem repetição, e `consultar` sempre. */
export function permissoesDoConector(brutas: unknown): PermissaoDoConector[] {
  const lista = Array.isArray(brutas) ? brutas : []
  const validas = new Set<PermissaoDoConector>(['consultar'])
  for (const p of lista) {
    if ((PERMISSOES_DO_CONECTOR as readonly string[]).includes(String(p))) validas.add(p as PermissaoDoConector)
  }
  return PERMISSOES_DO_CONECTOR.filter((p) => validas.has(p))
}

/* --------------------------------------------------------------- token --- */

/** Prefixo do token pessoal — reconhecível em log e em busca de vazamento. */
export const PREFIXO_DO_TOKEN_DO_CONECTOR = 'orb_'

/** Nome da ferramenta MCP → a permissão que ela exige. */
export const FERRAMENTAS_DO_CONECTOR = {
  meus_itens: 'consultar',
  buscar_itens: 'consultar',
  ver_item: 'consultar',
  catalogos: 'consultar',
  // SPEC-20261103-1822-tela-do-item-no-claude: o aviso curto para rotina
  resumo_do_dia: 'consultar',
  // SPEC-20261103-2217-evolucao-dos-itens-no-conector: criados × concluídos no tempo
  evolucao_dos_itens: 'consultar',
  atualizar_item: 'alterar',
  // SPEC-20261103-1958-conector-etapa-e-checklist
  mover_para_etapa: 'alterar',
  adicionar_checklist: 'alterar',
  // SPEC-20261105-0837-conector-vincula-linhas-de-checklist
  checklist_dos_filhos: 'alterar',
  vincular_linha_checklist: 'alterar',
  desvincular_linha_checklist: 'alterar',
  remover_linha_checklist: 'alterar',
  comentar_item: 'comentar',
  concluir_item: 'concluir',
  criar_item: 'criar',
  // SPEC-20261103-2009-campo-spec-e-campos-pelo-conector
  criar_campo_complementar: 'campos',
} as const satisfies Record<string, PermissaoDoConector>
export type FerramentaDoConector = keyof typeof FERRAMENTAS_DO_CONECTOR
